/* Geospatial analysis and vegetation monitoring services. */

import { request } from './http'

export interface AnalysisRunResultSummary {
  parcel_id: number
  crop: string
  observations_count: number
  m1?: {
    latest_ndvi_median: number
    latest_cover_pct: number | null
  }
  m2_anomaly?: {
    type: string
    severity: 'none' | 'mild' | 'moderate' | 'severe' | string
    delta_ndvi: number
    score: number
  }
  m3_recommendation?: {
    title: string
    action_type: string
    urgency: string
    summary: string
    field_checks: string[]
    citations: Array<{
      chunk_id: string
      title: string
      organization: string
      source_url: string
    }>
    provider: string
  }
}

export interface AnalysisRunResponse {
  run_id: string
  runId: string
  parcel_id: number
  parcelId: number
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'insufficient_data'
  attempt_count: number
  attemptCount: number
  error_message?: string | null
  errorMessage?: string | null
  result_summary?: AnalysisRunResultSummary | null
  resultSummary?: AnalysisRunResultSummary | null
  poll_url: string
  pollUrl: string
}

export interface SceneObservationItem {
  id: number
  scene_id?: number
  sceneId?: number
  vegetation_present?: boolean
  vegetationPresent?: boolean
  vegetation_cover_pct?: number
  vegetationCoverPct?: number
  ndvi_mean?: number | null
  ndviMean?: number | null
  ndvi_median?: number | null
  ndviMedian?: number | null
  ndvi_std?: number | null
  ndviStd?: number | null
  ndmi_median?: number | null
  ndmiMedian?: number | null
  valid_pixel_fraction?: number
  validPixelFraction?: number
  provenance?: Record<string, unknown> | null
}

export interface ParcelAnomalyItem {
  id: number
  baseline_scene_id?: number
  baselineSceneId?: number
  current_scene_id?: number
  currentSceneId?: number
  anomaly_type?: string
  anomalyType?: string
  severity: string
  score: number
  delta_ndvi?: number
  deltaNdvi?: number
  delta_ndmi?: number | null
  deltaNdmi?: number | null
  status: string
  evidence?: Record<string, unknown> | null
  limitations?: string | null
  detected_at?: string | null
  detectedAt?: string | null
}

function normalizeRun(raw: any): AnalysisRunResponse {
  if (!raw) return raw
  const runId = raw.run_id || raw.runId || ''
  const parcelId = raw.parcel_id ?? raw.parcelId ?? 0
  const summary = raw.result_summary || raw.resultSummary || null
  const pollUrl = raw.poll_url || raw.pollUrl || ''
  const errorMsg = raw.error_message || raw.errorMessage || null
  const attempts = raw.attempt_count ?? raw.attemptCount ?? 0
  return {
    ...raw,
    run_id: runId,
    runId,
    parcel_id: parcelId,
    parcelId,
    status: raw.status || 'queued',
    attempt_count: attempts,
    attemptCount: attempts,
    error_message: errorMsg,
    errorMessage: errorMsg,
    result_summary: summary,
    resultSummary: summary,
    poll_url: pollUrl,
    pollUrl,
  }
}

export async function enqueueAnalysisRun(parcelId: number): Promise<AnalysisRunResponse> {
  const raw = await request<any>('/analysis/runs', {
    method: 'POST',
    body: { parcel_id: parcelId },
  })
  return normalizeRun(raw)
}

export async function fetchAnalysisRun(runId: string, signal?: AbortSignal): Promise<AnalysisRunResponse> {
  const raw = await request<any>(`/analysis/runs/${runId}`, { signal })
  return normalizeRun(raw)
}

export async function fetchParcelObservations(
  parcelId: number,
  signal?: AbortSignal,
): Promise<SceneObservationItem[]> {
  const rawList = await request<any[]>(`/parcels/${parcelId}/observations`, { signal })
  if (!Array.isArray(rawList)) return []
  return rawList.map((item) => ({
    ...item,
    sceneId: item.scene_id ?? item.sceneId,
    vegetationPresent: item.vegetation_present ?? item.vegetationPresent,
    vegetationCoverPct: item.vegetation_cover_pct ?? item.vegetationCoverPct ?? 0,
    ndviMean: item.ndvi_mean ?? item.ndviMean,
    ndviMedian: item.ndvi_median ?? item.ndviMedian,
    ndviStd: item.ndvi_std ?? item.ndviStd,
    ndmiMedian: item.ndmi_median ?? item.ndmiMedian,
    validPixelFraction: item.valid_pixel_fraction ?? item.validPixelFraction ?? 1,
  }))
}

export async function fetchParcelAnomalies(
  parcelId: number,
  signal?: AbortSignal,
): Promise<ParcelAnomalyItem[]> {
  const rawList = await request<any[]>(`/parcels/${parcelId}/anomalies`, { signal })
  if (!Array.isArray(rawList)) return []
  return rawList.map((item) => ({
    ...item,
    baselineSceneId: item.baseline_scene_id ?? item.baselineSceneId,
    currentSceneId: item.current_scene_id ?? item.currentSceneId,
    anomalyType: item.anomaly_type ?? item.anomalyType ?? 'stable_canopy',
    deltaNdvi: item.delta_ndvi ?? item.deltaNdvi ?? 0,
    deltaNdmi: item.delta_ndmi ?? item.deltaNdmi,
    detectedAt: item.detected_at ?? item.detectedAt,
  }))
}
